import { useQuery } from '@tanstack/react-query';
import { Megaphone, Plug, Rocket, DollarSign, Target } from 'lucide-react';
import WidgetShell from '@/components/admin/dashboard/WidgetShell';
import StatCard from '@/components/admin/dashboard/StatCard';
import api from '@/lib/api';
import { formatCurrency } from '@/lib/utils';

// Mirrors the backend handler at
// functions/src/routes/admin/advertising/dashboard.ts (GET /admin/advertising/dashboard).
// NOTE: the AddieDashboard detail page's interface uses *_7d / roas_7d names the
// backend does NOT return — these are the real field names.
interface AdStats {
  connected_platforms: number;
  active_campaigns: number;
  draft_campaigns: number;
  paused_campaigns: number;
  total_spend: number;
  total_budget: number;
  ready_creatives: number;
}

interface AdPerformanceRow {
  date: string;
  impressions: number;
  clicks: number;
  // numeric columns can serialize as strings from postgres.js
  cost: number | string;
  conversions: number;
  revenue: number | string;
}

interface AdPlatformSummary {
  platform: string;
  name: string;
  status: string;
  campaign_count: number;
  active_count: number;
}

interface AdvertisingDashboardResponse {
  stats: AdStats;
  performance: AdPerformanceRow[];
  platforms: AdPlatformSummary[];
}

export default function AdvertisingSummaryWidget() {
  const { data, isLoading, error } = useQuery({
    queryKey: ['dashboard', 'advertising', 'summary'],
    queryFn: () => api.get<AdvertisingDashboardResponse>('/admin/advertising/dashboard?sandbox=false'),
    staleTime: 60000,
  });

  const conversions7d =
    data?.performance.reduce((sum, row) => sum + Number(row.conversions ?? 0), 0) ?? 0;

  const isEmpty =
    !!data &&
    data.stats.connected_platforms === 0 &&
    data.stats.active_campaigns === 0 &&
    data.stats.draft_campaigns === 0 &&
    data.stats.total_spend === 0;

  return (
    <WidgetShell
      title="Advertising"
      icon={<Megaphone />}
      to="/admin/advertising"
      isLoading={isLoading}
      error={error}
      isEmpty={isEmpty}
      emptyMessage="No ad platforms connected yet."
      emptyIcon={<Megaphone />}
    >
      {data && (
        <div className="grid grid-cols-2 gap-3">
          <StatCard
            title="Platforms"
            value={data.stats.connected_platforms}
            icon={<Plug />}
            tone="blue"
          />
          <StatCard
            title="Active campaigns"
            value={data.stats.active_campaigns}
            icon={<Rocket />}
            tone="green"
          />
          <StatCard
            title="Spend"
            value={formatCurrency(data.stats.total_spend)}
            icon={<DollarSign />}
            tone="purple"
          />
          <StatCard
            title="Conversions 7d"
            value={conversions7d}
            icon={<Target />}
            tone="teal"
          />
        </div>
      )}
    </WidgetShell>
  );
}
